import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { usePricingFetch, fetchNextPage } from '../../hooks/usePricingFetch';
import { regions, diskTypes, formatPrice } from '../../config/pricingConfig';

const DISK_SIZES = [
  { value: 'P4', label: 'P4 – 32 GiB' },
  { value: 'P6', label: 'P6 – 64 GiB' },
  { value: 'P10', label: 'P10 – 128 GiB' },
  { value: 'P15', label: 'P15 – 256 GiB' },
  { value: 'P20', label: 'P20 – 512 GiB' },
  { value: 'P30', label: 'P30 – 1 TiB' },
  { value: 'P40', label: 'P40 – 2 TiB' },
  { value: 'P50', label: 'P50 – 4 TiB' },
  { value: 'E4', label: 'E4 – 32 GiB' },
  { value: 'E6', label: 'E6 – 64 GiB' },
  { value: 'E10', label: 'E10 – 128 GiB' },
  { value: 'E15', label: 'E15 – 256 GiB' },
  { value: 'E20', label: 'E20 – 512 GiB' },
  { value: 'E30', label: 'E30 – 1 TiB' },
  { value: 'S4', label: 'S4 – 32 GiB' },
  { value: 'S6', label: 'S6 – 64 GiB' },
  { value: 'S10', label: 'S10 – 128 GiB' },
  { value: 'S15', label: 'S15 – 256 GiB' },
  { value: 'S20', label: 'S20 – 512 GiB' },
  { value: 'S30', label: 'S30 – 1 TiB' },
];

// Map disk type to product name fragment used in the API filter
const DISK_TYPE_PRODUCT_MAP = {
  'Premium SSD': 'Premium SSD Managed Disks',
  'Standard SSD': 'Standard SSD Managed Disks',
  'Standard HDD': 'Standard HDD Managed Disks',
  'Ultra Disk': 'Ultra Disks',
};

const ManagedDiskPricing = () => {
  const [region, setRegion] = useState('');
  const [diskType, setDiskType] = useState('Premium SSD');
  const [diskSize, setDiskSize] = useState('');
  const [customSize, setCustomSize] = useState('');
  const [searchParams, setSearchParams] = useState(null);
  const [extraItems, setExtraItems] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  const effectiveSize = diskSize || customSize;
  const productName = DISK_TYPE_PRODUCT_MAP[searchParams?.diskType] ?? 'Premium SSD Managed Disks';

  const filter = searchParams
    ? `armRegionName eq '${searchParams.region}' and serviceName eq 'Storage' and type eq 'Consumption' and contains(productName, '${productName}') and contains(skuName, '${searchParams.size}') and contains(meterName, 'Free') ne true and contains(meterName, 'Mount') ne true`
    : '';

  const { items, isLoading, error, nextPageLink } = usePricingFetch({
    queryKey: `manageddisk-${filter}`,
    filter,
    enabled: !!searchParams,
  });

  const handleFetch = () => {
    setSearchParams({ region, diskType, size: effectiveSize });
    setExtraItems([]);
    setNextPageUrl(null);
  };

  const handleLoadMore = async () => {
    const url = nextPageUrl || nextPageLink;
    if (!url) return;
    setLoadingMore(true);
    try {
      const data = await fetchNextPage(url);
      setExtraItems((prev) => [...prev, ...(data.Items || [])]);
      setNextPageUrl(data.NextPageLink || null);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const SortIndicator = ({ col }) =>
    sortKey === col ? (sortAsc ? ' ↑' : ' ↓') : null;

  const allItems = [...items, ...extraItems];
  const sortedItems = sortKey
    ? [...allItems].sort((a, b) => {
        const va = a[sortKey]; const vb = b[sortKey];
        return sortAsc
          ? (typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb)))
          : (typeof vb === 'number' ? vb - va : String(vb).localeCompare(String(va)));
      })
    : allItems;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Managed Disk Pricing</h2>
      <div className="flex flex-col sm:flex-row gap-2 mb-4 flex-wrap">
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Select Region" /></SelectTrigger>
          <SelectContent>{regions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={diskType} onValueChange={setDiskType}>
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="Disk Type" /></SelectTrigger>
          <SelectContent>{diskTypes.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={diskSize} onValueChange={(v) => { setDiskSize(v); setCustomSize(''); }}>
          <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="Disk Size (tier)" /></SelectTrigger>
          <SelectContent>{DISK_SIZES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input
          value={customSize}
          onChange={(e) => { setCustomSize(e.target.value); setDiskSize(''); }}
          placeholder="Or type size (e.g. P10)"
          className="w-full sm:w-40"
        />
        <Button onClick={handleFetch} disabled={isLoading || !region || !diskType || !effectiveSize}>
          {isLoading ? 'Loading…' : 'Fetch Prices'}
        </Button>
      </div>

      {error && <div className="text-red-500 mb-4 text-sm">{error.message}</div>}
      {isLoading && <SkeletonTable rows={4} cols={3} />}

      {!isLoading && sortedItems.length > 0 && (
        <>
          <p className="text-sm text-gray-500 mb-2">Showing {sortedItems.length} result(s)</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('skuName')}>SKU Name<SortIndicator col="skuName" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('meterName')}>Meter<SortIndicator col="meterName" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('unitPrice')}>Unit Price<SortIndicator col="unitPrice" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map((price) => (
                <TableRow key={`${price.skuId}-${price.meterName}`}>
                  <TableCell>{price.skuName}</TableCell>
                  <TableCell>{price.meterName}</TableCell>
                  <TableCell>{formatPrice(price.unitPrice, price.currencyCode, price.unitOfMeasure)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(nextPageUrl || nextPageLink) && (
            <div className="mt-4 text-center">
              <Button onClick={handleLoadMore} disabled={loadingMore} className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                {loadingMore ? 'Loading…' : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManagedDiskPricing;
